import { useMemo } from 'react'
import {
  selectCurrentContainer,
  useSequencerStore,
} from '@/stores/sequencerStore'
import { useMidiStore, type HwMidiRoute } from '@/stores/midiStore'

/**
 * Effective MIDI snapshot — merges the global `midiStore` values
 * with an optional per-project override. Every consumer (MidiRouter,
 * Web MIDI dispatch, sequencer per-output latency reads) routes
 * through here so "which routes are really in effect right now" has
 * exactly one answer across the app.
 *
 * The merge rule is field-level: if `project.midiConfig.routes` is
 * non-null, it wins; if it's null (or `midiConfig` is absent), the
 * global list is used. Same for `outputLatencyMs`. `padNoteBindings`
 * stays global — MIDI Learn assignments describe hardware layout,
 * not song-specific wiring.
 */
export interface EffectiveMidi {
  routes: HwMidiRoute[]
  outputLatencyMs: Record<string, number>
  /** Where the currently-effective values originate — used to render
   *  the "Project override" badge vs "Global routing" header. */
  source: 'global' | 'project-override'
  /** True when the active project has populated `midiConfig` — even
   *  if only one sub-field is overridden. Drives the Customize /
   *  Reset affordances in the MIDI Router UI. */
  hasOverride: boolean
  /** The active project's id if one is selected, else null. Lets
   *  the UI show "Routing for {projectName}" vs "Global routing". */
  activeProjectId: string | null
  activeProjectName: string | null
}

export function useEffectiveMidi(): EffectiveMidi {
  const routesGlobal = useMidiStore(s => s.routes)
  const latencyGlobal = useMidiStore(s => s.outputLatencyMs)
  const container = useSequencerStore(selectCurrentContainer)

  return useMemo(() => {
    const override = container?.midiConfig
    const hasOverride = !!override
    const overrideRoutes =
      override && override.routes !== null ? override.routes : null
    const overrideLatency =
      override && override.outputLatencyMs !== null
        ? override.outputLatencyMs
        : null
    return {
      routes: overrideRoutes
        ? overrideRoutes.map(r => ({
            inputId: r.inputId,
            outputId: r.outputId,
          }))
        : routesGlobal,
      outputLatencyMs: overrideLatency ?? latencyGlobal,
      source: overrideRoutes || overrideLatency ? 'project-override' : 'global',
      hasOverride,
      activeProjectId: container?.id ?? null,
      activeProjectName: container?.name ?? null,
    }
  }, [container, routesGlobal, latencyGlobal])
}
